import { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import { Toaster } from 'sonner';
import { Skeleton } from './components/ui/skeleton';
import { Navigation } from './components/Navigation';
import {
  TipInbox,
  Assignments,
  SourceProfile,
  Login,
  Register,
  Conversation,
  NewTip,
  EditAssignment,
  EditTip,
  AssignmentDetails,
} from './pages';

import {
  isAuthenticatedAtom,
  authLoadingAtom,
  initializeAuthAtom,
} from './stores/authStore';

import { testApiConnection } from './utils/debugUtils';
import './App.css';

function AuthenticatedRoutes() {
  // Test API connection on app start
  useEffect(() => {
    testApiConnection();
  }, []);

  return (
    <div className='h-screen w-full flex bg-background overflow-hidden max-w-7xl mx-auto'>
      <Navigation />
      <main className='flex-1'>
        <Routes>
          <Route path='/' element={<Navigate to='/inbox' replace />} />
          <Route path='/inbox' element={<TipInbox />} />
          <Route path='/inbox/new' element={<NewTip />} />
          <Route path='/inbox/:id/edit' element={<EditTip />} />
          <Route path='/sources' element={<SourceProfile />} />
          <Route path='/assignments' element={<Assignments />} />
          <Route path='/assignments/:id' element={<AssignmentDetails />} />
          <Route path='/assignments/:id/edit' element={<EditAssignment />} />
          <Route path='*' element={<Navigate to='/inbox' replace />} />
        </Routes>
      </main>
    </div>
  );
}

function UnauthenticatedRoutes() {
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  return (
    <div className='h-screen w-full overflow-hidden'>
      <Routes>
        <Route
          path='*'
          element={
            authMode === 'register' ? (
              <Register onSwitchToLogin={() => setAuthMode('login')} />
            ) : (
              <Login onSwitchToRegister={() => setAuthMode('register')} />
            )
          }
        />
      </Routes>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className='h-screen w-full bg-background flex items-center justify-center overflow-hidden'>
      <div className='text-center space-y-4'>
        <div className='space-y-2'>
          <Skeleton className='h-8 w-48 mx-auto' />
          <Skeleton className='h-4 w-32 mx-auto' />
        </div>
        <div className='space-y-2'>
          <Skeleton className='h-4 w-64 mx-auto' />
          <Skeleton className='h-4 w-48 mx-auto' />
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const isLoading = useAtomValue(authLoadingAtom);
  const initializeAuth = useSetAtom(initializeAuthAtom);
  const location = useLocation();
  const isConversationRoute = location.pathname.startsWith('/conversation/');

  useEffect(() => {
    // Skip auth initialization on public conversation route to avoid
    // calling /auth/users/me for unauthenticated interview guests.
    if (isConversationRoute) {
      return;
    }

    initializeAuth();
  }, [initializeAuth, isConversationRoute]);

  // Always allow the public conversation route, even while auth is
  // initializing or skipped.
  if (isConversationRoute) {
    return (
      <Routes>
        <Route path='/conversation/:id' element={<Conversation />} />
      </Routes>
    );
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Authenticated app shell for all other routes */}
      {isAuthenticated ? (
        <Route path='*' element={<AuthenticatedRoutes />} />
      ) : (
        <Route path='*' element={<UnauthenticatedRoutes />} />
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <div className='h-screen w-full overflow-hidden'>
      <Provider>
        <BrowserRouter>
          <AppContent />
          <Toaster />
        </BrowserRouter>
      </Provider>
    </div>
  );
}
